class ValesstraV2Collection {
  constructor(root) {
    this.root = root;
    this.drawer = root.querySelector('[data-v2-filter-drawer]');
    this.drawerTrigger = null;
    this.bindDrawer();
    this.bindSorting();
    this.bindCardFeedback();
  }

  bindDrawer() {
    if (!this.drawer) return;

    this.root.querySelectorAll('[data-v2-filter-open]').forEach((button) => {
      button.addEventListener('click', () => {
        this.drawerTrigger = button;
        this.drawer.showModal();
        document.documentElement.classList.add('v2-filter-drawer-open');

        const targetId = button.dataset.v2FilterTarget;
        const target = targetId ? document.getElementById(targetId) : null;
        if (target) {
          target.open = true;
          requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
        }

        const firstControl = this.drawer.querySelector('[data-v2-filter-close]');
        if (firstControl) firstControl.focus();
      });
    });

    this.drawer.querySelector('[data-v2-filter-close]')?.addEventListener('click', () => this.drawer.close());

    this.drawer.addEventListener('click', (event) => {
      if (event.target === this.drawer) this.drawer.close();
    });

    this.drawer.addEventListener('close', () => {
      document.documentElement.classList.remove('v2-filter-drawer-open');
      this.drawerTrigger?.focus();
    });
  }

  bindSorting() {
    this.root.querySelectorAll('[data-v2-sort-select]').forEach((select) => {
      select.addEventListener('change', () => select.form?.requestSubmit());
    });
  }

  bindCardFeedback() {
    const cards = Array.from(this.root.querySelectorAll('[data-v2-product-card]'));

    const setButtonLabel = (button, label) => {
      const labelElement = button?.querySelector('[data-v2-card-button-label]');
      if (labelElement) labelElement.textContent = label;
    };

    const resetCard = (card) => {
      window.clearTimeout(card.v2CardResetTimer);
      window.clearTimeout(card.v2CardTimeout);
      card.querySelectorAll('[data-v2-card-button-label]').forEach((label) => {
        label.textContent = card.dataset.v2CardAdd;
      });
      delete card.dataset.v2PendingVariant;
      card.v2PendingButton = null;
    };

    const failCard = (card, message) => {
      window.clearTimeout(card.v2CardTimeout);
      const error = card.querySelector('.v2-product-card__error');
      if (error) {
        const errorText = error.querySelector('.product-form__error-message');
        if (errorText && !errorText.textContent.trim()) errorText.textContent = message || card.dataset.v2CardError;
        error.hidden = false;
      }
      setButtonLabel(card.v2PendingButton, card.dataset.v2CardAdd);
      delete card.dataset.v2PendingVariant;
      card.v2PendingButton = null;
    };

    cards.forEach((card) => {
      card.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
        form.addEventListener('submit', () => {
          const button = form.querySelector('[type="submit"]');
          const variantId = form.querySelector('[name="id"]')?.value;
          if (!variantId || button?.disabled || button?.getAttribute('aria-disabled') === 'true') return;
          const error = form.closest('product-form')?.querySelector('.v2-product-card__error');
          if (error) error.hidden = true;
          card.dataset.v2PendingVariant = String(variantId);
          card.v2PendingButton = button;
          setButtonLabel(button, card.dataset.v2CardAdding);
          window.clearTimeout(card.v2CardTimeout);
          card.v2CardTimeout = window.setTimeout(() => failCard(card), 15000);
        }, { capture: true });
      });
    });

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
      const variantId = String(payload?.productVariantId || '');
      cards.forEach((card) => {
        if (!card.dataset.v2PendingVariant || card.dataset.v2PendingVariant !== variantId) return;
        window.clearTimeout(card.v2CardTimeout);
        card.querySelectorAll('[data-v2-card-button-label]').forEach((label) => {
          label.textContent = card.dataset.v2CardAdded;
        });
        const panel = card.querySelector('[data-v2-options]');
        if (panel?.open) {
          panel.dataset.restoreFocus = 'false';
          panel.close();
        }
        card.v2CardResetTimer = window.setTimeout(() => resetCard(card), 1600);
      });
    });

    subscribe(PUB_SUB_EVENTS.cartError, (payload) => {
      const variantId = String(payload?.productVariantId || '');
      cards.forEach((card) => {
        if (!card.dataset.v2PendingVariant || card.dataset.v2PendingVariant !== variantId) return;
        failCard(card, payload?.message || payload?.errors);
      });
    });
  }
}

const initValesstraV2Collections = (scope = document) => {
  const roots = [];
  if (scope.matches?.('[data-v2-main-collection], [data-v2-product-rail]')) roots.push(scope);
  roots.push(...scope.querySelectorAll('[data-v2-main-collection], [data-v2-product-rail]'));

  roots.forEach((root) => {
    if (root.dataset.v2ProductCardsInitialized === 'true') return;
    root.dataset.v2ProductCardsInitialized = 'true';
    new ValesstraV2Collection(root);
  });
};

const revealActiveCategoryRailItem = (scope = document) => {
  if (!window.matchMedia('(max-width: 768px)').matches) return;

  const rails = [];
  if (scope.matches?.('.v2-category-rail')) rails.push(scope);
  rails.push(...scope.querySelectorAll('.v2-category-rail'));

  rails.forEach((rail) => {
    const activeItem = rail.querySelector('[aria-current="page"]');
    if (!activeItem) return;

    const railBounds = rail.getBoundingClientRect();
    const activeBounds = activeItem.getBoundingClientRect();
    let targetScrollLeft = rail.scrollLeft;

    if (activeBounds.left < railBounds.left) {
      targetScrollLeft += activeBounds.left - railBounds.left;
    } else if (activeBounds.right > railBounds.right) {
      targetScrollLeft += activeBounds.right - railBounds.right;
    } else {
      return;
    }

    const maximumScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    rail.scrollLeft = Math.max(0, Math.min(targetScrollLeft, maximumScrollLeft));
  });
};

const initValesstraV2CollectionPage = (scope = document) => {
  initValesstraV2Collections(scope);
  revealActiveCategoryRailItem(scope);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initValesstraV2CollectionPage());
} else {
  initValesstraV2CollectionPage();
}

window.addEventListener('pageshow', () => revealActiveCategoryRailItem());
document.addEventListener('shopify:section:load', (event) => initValesstraV2CollectionPage(event.target));
